import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Play, Star } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { DailyChallenge } from '../../utils/dailyChallenge';
import { DjayAvatar, DjayVariant } from '../../mascot';
import { soundManager } from '../../utils/soundManager';
interface DailyChallengeCardProps {
  challenge: DailyChallenge;
  djayVariant: DjayVariant;
  onStart: () => void;
}
export function DailyChallengeCard({
  challenge,
  djayVariant,
  onStart
}: DailyChallengeCardProps) {
  const {
    type,
    title,
    description,
    icon,
    targetCount,
    currentCount,
    rewardXP,
    isCompleted
  } = challenge;
  const progressPercent = Math.min(100, currentCount / targetCount * 100);
  // Determine colors based on challenge type
  let colorTheme = {
    border: 'border-amber-200',
    bg: 'bg-amber-50',
    text: 'text-amber-900',
    textMuted: 'text-amber-700',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    progressBg: 'bg-amber-200',
    progressFill: 'bg-amber-500',
    button: 'bg-amber-500 hover:bg-amber-600'
  };
  if (type === 'snelheid') {
    colorTheme = {
      border: 'border-blue-200',
      bg: 'bg-blue-50',
      text: 'text-blue-900',
      textMuted: 'text-blue-700',
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600',
      progressBg: 'bg-blue-200',
      progressFill: 'bg-blue-500',
      button: 'bg-blue-500 hover:bg-blue-600'
    };
  } else if (type === 'mix') {
    colorTheme = {
      border: 'border-purple-200',
      bg: 'bg-purple-50',
      text: 'text-purple-900',
      textMuted: 'text-purple-700',
      iconBg: 'bg-purple-100',
      iconText: 'text-purple-600',
      progressBg: 'bg-purple-200',
      progressFill: 'bg-purple-500',
      button: 'bg-purple-500 hover:bg-purple-600'
    };
  } else if (type === 'herhaal') {
    colorTheme = {
      border: 'border-teal-200',
      bg: 'bg-teal-50',
      text: 'text-teal-900',
      textMuted: 'text-teal-700',
      iconBg: 'bg-teal-100',
      iconText: 'text-teal-600',
      progressBg: 'bg-teal-200',
      progressFill: 'bg-teal-500',
      button: 'bg-teal-500 hover:bg-teal-600'
    };
  }
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    type: 'spring',
    bounce: 0.4
  }} data-id="element-1218">
      <Card className={`p-4 sm:p-5 border-2 ${isCompleted ? 'border-emerald-200 bg-emerald-50' : `${colorTheme.border} ${colorTheme.bg}`} rounded-2xl shadow-sm relative overflow-hidden`} data-id="element-1219">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center" data-id="element-1220">
          {/* Icon / Avatar */}
          <div className="flex-shrink-0" data-id="element-1221">
            {isCompleted ? <div className="w-14 h-14 relative" data-id="element-1222">
                <DjayAvatar emotion="celebrating" variant={djayVariant} size="lg" className="w-full h-full drop-shadow-sm" data-id="element-1223" />
              </div> : <div className={`w-14 h-14 rounded-2xl ${colorTheme.iconBg} flex items-center justify-center text-3xl shadow-inner`} data-id="element-1224">
                {icon}
              </div>}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0" data-id="element-1225">
            <div className="flex items-center gap-2 mb-1" data-id="element-1226">
              <h3 className={`text-base font-black ${isCompleted ? 'text-emerald-900' : colorTheme.text}`} data-id="element-1227">
                {title}
              </h3>
              <div className="px-2 py-0.5 rounded-full bg-white/60 border border-white/80 text-xs font-bold flex items-center gap-1 shadow-sm" data-id="element-1228">
                <Star className="w-3 h-3 text-amber-500 fill-amber-500" data-id="element-1229" />
                <span className="text-slate-700" data-id="element-1230">+{rewardXP} XP</span>
              </div>
            </div>
            <p className={`text-sm font-medium ${isCompleted ? 'text-emerald-700' : colorTheme.textMuted} mb-3`} data-id="element-1231">
              {isCompleted ? 'Uitdaging voltooid! Bonus XP verdiend.' : description}
            </p>

            {/* Progress Bar */}
            <div className="flex items-center gap-3" data-id="element-1232">
              <div className={`flex-1 h-2.5 rounded-full overflow-hidden ${isCompleted ? 'bg-emerald-200' : colorTheme.progressBg} shadow-inner`} data-id="element-1233">
                <motion.div initial={{
                width: 0
              }} animate={{
                width: `${progressPercent}%`
              }} transition={{
                duration: 1,
                ease: 'easeOut'
              }} className={`h-full rounded-full ${isCompleted ? 'bg-emerald-500' : colorTheme.progressFill}`} data-id="element-1234" />
              </div>
              <span className={`text-xs font-black ${isCompleted ? 'text-emerald-700' : colorTheme.text} whitespace-nowrap`} data-id="element-1235">
                {currentCount} / {targetCount}
              </span>
            </div>
          </div>

          {/* Action Button */}
          <div className="flex-shrink-0 w-full sm:w-auto mt-2 sm:mt-0" data-id="element-1236">
            {isCompleted ? <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-100 text-emerald-700 font-bold border border-emerald-200" data-id="element-1237">
                <CheckCircle2 className="w-5 h-5" data-id="element-1238" />
                <span data-id="element-1239">Voltooid!</span>
              </div> : <Button onClick={() => {
            soundManager.playNavigate();
            onStart();
          }} className={`w-full sm:w-auto ${colorTheme.button} text-white shadow-sm border-none`} data-id="element-1240">
                <Play className="w-4 h-4 mr-2 fill-current" data-id="element-1241" />
                Start
              </Button>}
          </div>
        </div>
      </Card>
    </motion.div>;
}